/**
 * HVN: Saved Properties block — editor component.
 *
 * The preview is the real server render (ServerSideRender via BlockPreview), so
 * the editor is WYSIWYG. When the editing user has no saved properties the
 * renderer shows a sample of recent listings so the layout is visible. All
 * inputs are neutralized inside the editor by the shared EditorPreview guard.
 *
 * @package
 * @since   3.5.0
 */

import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	ToggleControl,
	RangeControl,
	TextControl,
} from '@wordpress/components';

import BlockPreview from '../shared/BlockPreview';

const LAYOUT_OPTIONS = [
	{ label: __( 'Grid', 'havenlytics' ), value: 'grid' },
	{ label: __( 'List', 'havenlytics' ), value: 'list' },
	{ label: __( 'Compact', 'havenlytics' ), value: 'compact' },
	{ label: __( 'Carousel', 'havenlytics' ), value: 'carousel' },
];

const COLUMN_OPTIONS = [
	{ label: '1', value: 1 },
	{ label: '2', value: 2 },
	{ label: '3', value: 3 },
	{ label: '4', value: 4 },
	{ label: '6', value: 6 },
];

const ORDER_OPTIONS = [
	{ label: __( 'Newest saved', 'havenlytics' ), value: 'newest' },
	{ label: __( 'Oldest saved', 'havenlytics' ), value: 'oldest' },
	{ label: __( 'Recently updated', 'havenlytics' ), value: 'recently_updated' },
	{ label: __( 'Property title', 'havenlytics' ), value: 'title' },
	{ label: __( 'Price', 'havenlytics' ), value: 'price' },
	{ label: __( 'Default', 'havenlytics' ), value: 'default' },
];

const PAGINATION_OPTIONS = [
	{ label: __( 'Numbered pages', 'havenlytics' ), value: 'numbered' },
	{ label: __( 'None (show one page)', 'havenlytics' ), value: 'none' },
];

const LOGGED_OUT_OPTIONS = [
	{ label: __( 'Sign-in form (Authentication block)', 'havenlytics' ), value: 'auth' },
	{ label: __( 'Compact sign-in prompt', 'havenlytics' ), value: 'message' },
];

export default function Edit( { attributes, setAttributes } ) {
	const {
		layout,
		columns,
		postsPerPage,
		orderby,
		paginationMode,
		showTitle,
		sectionTitle,
		showDescription,
		sectionDescription,
		loggedOutMode,
		emptyButtonText,
		visibleSlides,
		autoplay,
		showNav,
		showDots,
	} = attributes;

	const blockProps = useBlockProps();
	const isCarousel = layout === 'carousel';

	return (
		<>
			<InspectorControls>
				<PanelBody title={ __( 'General', 'havenlytics' ) } initialOpen={ true }>
					<p className="components-base-control__help">
						{ __(
							'Favorites use the same Property Card Builder as archive listings.',
							'havenlytics'
						) }
					</p>
					<SelectControl
						label={ __( 'Layout', 'havenlytics' ) }
						value={ layout }
						options={ LAYOUT_OPTIONS }
						onChange={ ( value ) => setAttributes( { layout: value } ) }
						__nextHasNoMarginBottom
					/>
					{ ! isCarousel && (
						<SelectControl
							label={ __( 'Columns', 'havenlytics' ) }
							value={ columns }
							options={ COLUMN_OPTIONS }
							onChange={ ( value ) => setAttributes( { columns: parseInt( value, 10 ) } ) }
							__nextHasNoMarginBottom
						/>
					) }
					<RangeControl
						label={ __( 'Properties per page', 'havenlytics' ) }
						value={ postsPerPage }
						min={ 1 }
						max={ 48 }
						onChange={ ( value ) => setAttributes( { postsPerPage: value } ) }
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Ordering', 'havenlytics' ) }
						value={ orderby }
						options={ ORDER_OPTIONS }
						onChange={ ( value ) => setAttributes( { orderby: value } ) }
						__nextHasNoMarginBottom
					/>
					{ ! isCarousel && (
						<SelectControl
							label={ __( 'Pagination', 'havenlytics' ) }
							value={ paginationMode }
							options={ PAGINATION_OPTIONS }
							onChange={ ( value ) => setAttributes( { paginationMode: value } ) }
							help={ __( 'Numbered pages reload the page — saved lists are user-specific and are never paged through the shared archive AJAX.', 'havenlytics' ) }
							__nextHasNoMarginBottom
						/>
					) }
				</PanelBody>

				<PanelBody title={ __( 'Header', 'havenlytics' ) } initialOpen={ false }>
					<ToggleControl
						label={ __( 'Show section title', 'havenlytics' ) }
						checked={ !! showTitle }
						onChange={ ( value ) => setAttributes( { showTitle: value } ) }
						__nextHasNoMarginBottom
					/>
					{ showTitle && (
						<TextControl
							label={ __( 'Section title', 'havenlytics' ) }
							value={ sectionTitle }
							onChange={ ( value ) => setAttributes( { sectionTitle: value } ) }
							__nextHasNoMarginBottom
						/>
					) }
					<ToggleControl
						label={ __( 'Show section description', 'havenlytics' ) }
						checked={ !! showDescription }
						onChange={ ( value ) => setAttributes( { showDescription: value } ) }
						__nextHasNoMarginBottom
					/>
					{ showDescription && (
						<TextControl
							label={ __( 'Section description', 'havenlytics' ) }
							value={ sectionDescription }
							onChange={ ( value ) => setAttributes( { sectionDescription: value } ) }
							__nextHasNoMarginBottom
						/>
					) }
				</PanelBody>

				{ isCarousel && (
					<PanelBody title={ __( 'Carousel', 'havenlytics' ) } initialOpen={ false }>
						<RangeControl
							label={ __( 'Visible slides', 'havenlytics' ) }
							value={ visibleSlides }
							min={ 1 }
							max={ 5 }
							onChange={ ( value ) => setAttributes( { visibleSlides: value } ) }
							__nextHasNoMarginBottom
						/>
						<ToggleControl
							label={ __( 'Autoplay', 'havenlytics' ) }
							checked={ !! autoplay }
							onChange={ ( value ) => setAttributes( { autoplay: value } ) }
							__nextHasNoMarginBottom
						/>
						<ToggleControl
							label={ __( 'Show navigation arrows', 'havenlytics' ) }
							checked={ !! showNav }
							onChange={ ( value ) => setAttributes( { showNav: value } ) }
							__nextHasNoMarginBottom
						/>
						<ToggleControl
							label={ __( 'Show dots', 'havenlytics' ) }
							checked={ !! showDots }
							onChange={ ( value ) => setAttributes( { showDots: value } ) }
							__nextHasNoMarginBottom
						/>
					</PanelBody>
				) }

				<PanelBody title={ __( 'Empty & signed-out state', 'havenlytics' ) } initialOpen={ false }>
					<SelectControl
						label={ __( 'When signed out', 'havenlytics' ) }
						value={ loggedOutMode }
						options={ LOGGED_OUT_OPTIONS }
						onChange={ ( value ) => setAttributes( { loggedOutMode: value } ) }
						help={ __( 'Reuses the HVN: Authentication block — no separate login form is created.', 'havenlytics' ) }
						__nextHasNoMarginBottom
					/>
					<TextControl
						label={ __( 'Empty-state button text', 'havenlytics' ) }
						value={ emptyButtonText }
						onChange={ ( value ) => setAttributes( { emptyButtonText: value } ) }
						__nextHasNoMarginBottom
					/>
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				<BlockPreview
					block="havenlytics/saved-properties"
					attributes={ attributes }
				/>
			</div>
		</>
	);
}
